import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
  type Timestamp,
} from 'firebase/firestore'
import { db } from './firebase'

export interface WorkTypeCategory {
  id: string
  name: string
  color: string
  order: number
  createdAt?: Timestamp
  updatedAt?: Timestamp
}

const COLLECTION = 'workTypeCategories'
const WORK_TYPES = 'workTypes'
const BATCH_LIMIT = 400

/** Palette used when seeding / picking colours (same set as team colours). */
export const WORK_TYPE_CATEGORY_COLORS = [
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#eab308',
  '#84cc16',
  '#22c55e',
  '#10b981',
  '#14b8a6',
  '#06b6d4',
  '#0ea5e9',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#a855f7',
  '#d946ef',
  '#ec4899',
  '#f43f5e',
]

export const UNCATEGORISED_COLOR = '#9ca3af'

export function slugifyCategoryId(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60)
  return slug ? `cat-${slug}` : `cat-${Date.now()}`
}

export class WorkTypeCategoriesService {
  private static instance: WorkTypeCategoriesService

  static getInstance(): WorkTypeCategoriesService {
    if (!WorkTypeCategoriesService.instance) {
      WorkTypeCategoriesService.instance = new WorkTypeCategoriesService()
    }
    return WorkTypeCategoriesService.instance
  }

  async getAllCategories(): Promise<WorkTypeCategory[]> {
    const snapshot = await getDocs(
      query(collection(db, COLLECTION), orderBy('name'))
    )
    return snapshot.docs.map((d) => {
      const data = d.data()
      return {
        id: d.id,
        name: String(data.name || ''),
        color: String(data.color || UNCATEGORISED_COLOR),
        order: typeof data.order === 'number' ? data.order : 0,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      } satisfies WorkTypeCategory
    })
  }

  async updateCategory(
    categoryId: string,
    updates: { name?: string; color?: string; order?: number }
  ): Promise<void> {
    const payload: {
      updatedAt: ReturnType<typeof serverTimestamp>
      name?: string
      color?: string
      order?: number
    } = {
      updatedAt: serverTimestamp(),
    }
    if (updates.name !== undefined) payload.name = updates.name.trim()
    if (updates.color !== undefined) payload.color = updates.color
    if (updates.order !== undefined) payload.order = updates.order
    await updateDoc(doc(db, COLLECTION, categoryId), payload)
  }

  /**
   * Delete a category and reassign its work types.
   * `transferToCategoryId` null → leave Uncategorised.
   */
  async deleteCategory(
    categoryId: string,
    transferToCategoryId: string | null
  ): Promise<{ reassigned: number }> {
    const workSnap = await getDocs(collection(db, WORK_TYPES))
    const toUpdate = workSnap.docs.filter(
      (d) => d.data().categoryId === categoryId
    )

    let reassigned = 0
    for (let i = 0; i < toUpdate.length; i += BATCH_LIMIT) {
      const chunk = toUpdate.slice(i, i + BATCH_LIMIT)
      const batch = writeBatch(db)
      for (const d of chunk) {
        batch.update(d.ref, {
          categoryId: transferToCategoryId,
          updatedAt: serverTimestamp(),
        })
        reassigned++
      }
      await batch.commit()
    }

    await deleteDoc(doc(db, COLLECTION, categoryId))
    return { reassigned }
  }

  async createCategory(input: {
    name: string
    color: string
  }): Promise<string> {
    const id = slugifyCategoryId(input.name)
    const batch = writeBatch(db)
    batch.set(doc(db, COLLECTION, id), {
      name: input.name.trim(),
      color: input.color,
      order: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    await batch.commit()
    return id
  }

  /**
   * Seed categories from distinct work-type `pipedriveCategory` values and
   * set `categoryId` on each work type. Idempotent for existing category docs.
   */
  async seedFromWorkTypeLabels(
    labels: string[],
    workTypeCategoryById: Record<string, string | null>
  ): Promise<{ categoriesWritten: number; workTypesUpdated: number }> {
    const unique = [...new Set(labels.filter(Boolean))].sort((a, b) =>
      a.localeCompare(b)
    )

    let categoriesWritten = 0
    const nameToId = new Map<string, string>()
    const catBatch = writeBatch(db)
    unique.forEach((name, index) => {
      const id = slugifyCategoryId(name)
      nameToId.set(name, id)
      catBatch.set(
        doc(db, COLLECTION, id),
        {
          name,
          color: WORK_TYPE_CATEGORY_COLORS[index % WORK_TYPE_CATEGORY_COLORS.length],
          order: index,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      )
      categoriesWritten++
    })
    if (categoriesWritten > 0) await catBatch.commit()

    let workTypesUpdated = 0
    const entries = Object.entries(workTypeCategoryById)
    for (let i = 0; i < entries.length; i += BATCH_LIMIT) {
      const chunk = entries.slice(i, i + BATCH_LIMIT)
      const batch = writeBatch(db)
      for (const [workTypeId, label] of chunk) {
        const categoryId = label ? nameToId.get(label) ?? null : null
        batch.set(
          doc(db, WORK_TYPES, workTypeId),
          {
            categoryId,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        )
        workTypesUpdated++
      }
      await batch.commit()
    }

    return { categoriesWritten, workTypesUpdated }
  }
}
